import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import axios from 'axios';
import api, { type TpaRun } from '../api/client';

const PAGE_SIZE = 50;

const PINNED_COLUMNS: Array<{ key: string; label: string }> = [
  { key: 'run_id', label: 'run_id' },
  { key: 'experiment_id', label: 'experiment' },
  { key: 'group_id', label: 'group' },
  { key: 'fish_id', label: 'fish' },
  { key: 'section', label: 'section' },
  { key: 'surface', label: 'surface' },
  { key: 'days_since_kill', label: 'days' },
  { key: 'frozen_state', label: 'frozen' },
  { key: 'label', label: 'label' },
  { key: 'test_type', label: 'test' },
  { key: 'saved_at', label: 'saved at' },
];

function buildColumns(runs: TpaRun[]): Array<{ key: string; label: string }> {
  const pinnedKeys = new Set(PINNED_COLUMNS.map((c) => c.key));
  const extras = new Set<string>();

  for (const row of runs) {
    for (const key of Object.keys(row)) {
      if (pinnedKeys.has(key)) continue;
      const v = row[key as keyof TpaRun];
      if (v !== null && typeof v === 'object') continue;
      extras.add(key);
    }
  }

  const extraCols = Array.from(extras)
    .sort()
    .map((key) => ({ key, label: key }));
  return [...PINNED_COLUMNS, ...extraCols];
}

const LINE_COLORS = ['#8ecae6', '#2a9d8f', '#e63946', '#023047', '#f4a261', '#6a4c93', '#457b9d'];

function surfaceOf(row: TpaRun): string | null {
  const fromField = row.surface?.trim();
  if (fromField) return fromField;
  const label = row.label?.trim();
  if (!label) return null;
  const parts = label.split('_');
  if (parts.length < 3) return null;
  const tail = parts[parts.length - 1]?.trim();
  return tail || null;
}

function cellValue(row: TpaRun, key: string): string {
  if (key === 'surface') {
    return surfaceOf(row) || '—';
  }
  const v = row[key as keyof TpaRun];
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'number' && Number.isFinite(v)) {
    if (key === 'peak_force_gf') return v.toFixed(1);
    if (key === 'duration_s') return v.toFixed(2);
    return String(v);
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function errorDetail(e: unknown): string {
  if (axios.isAxiosError(e)) {
    const detail = (e.response?.data as { detail?: string } | undefined)?.detail;
    return detail || e.message;
  }
  return e instanceof Error ? e.message : 'Request failed';
}

function lotId(row: TpaRun): string {
  const experiment = (row.experiment_id || '').trim();
  const group = (row.group_id || '').trim();
  const version = Number(row.schema_version || 1);
  if (version >= 2 || experiment) return experiment;
  return group;
}

function seriesName(run: TpaRun, all: TpaRun[]): string {
  const day = run.days_since_kill;
  const base = day != null ? `Day ${day}` : (run.saved_at?.slice(0, 10) || run.run_id.slice(0, 8));
  const sameDay = all.filter((r) => r.days_since_kill === day).length > 1;
  return sameDay ? `${base} (${run.run_id.slice(0, 8)})` : base;
}

function TpaViewer() {
  const [experimentId, setExperimentId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [fishId, setFishId] = useState('');
  const [section, setSection] = useState('');
  const [surface, setSurface] = useState('');
  const [species, setSpecies] = useState('');
  const [frozenState, setFrozenState] = useState('');
  const [allRuns, setAllRuns] = useState<TpaRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [series, setSeries] = useState<TpaRun[]>([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartTitle, setChartTitle] = useState('');

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: {
        experiment_id?: string;
        group_id?: string;
        fish_id?: string;
        section?: string;
        surface?: string;
        species?: string;
        frozen_state?: string;
      } = {};
      if (experimentId.trim()) params.experiment_id = experimentId.trim();
      if (groupId.trim()) params.group_id = groupId.trim();
      if (fishId.trim()) params.fish_id = fishId.trim();
      if (section.trim()) params.section = section.trim();
      if (surface.trim()) params.surface = surface.trim();
      if (species.trim()) params.species = species.trim();
      if (frozenState.trim()) params.frozen_state = frozenState.trim();
      const res = await api.tpa.listRuns(params);
      setAllRuns(res.data.runs || []);
      setCurrentPage(1);
    } catch (e: unknown) {
      setError(errorDetail(e) || 'Failed to load TPA runs');
      setAllRuns([]);
    } finally {
      setLoading(false);
    }
  }, [experimentId, groupId, fishId, section, surface, species, frozenState]);

  useEffect(() => {
    loadList();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- initial load only

  const totalPages = Math.max(1, Math.ceil(allRuns.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, allRuns.length);
  const visibleRuns = allRuns.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    const last = Math.max(1, totalPages);
    if (page >= 1 && page <= last) setCurrentPage(page);
  };

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const columns = useMemo(() => buildColumns(allRuns), [allRuns]);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(
    () => new Set(PINNED_COLUMNS.map((c) => c.key))
  );
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  const visibleColumns = useMemo(
    () => columns.filter((c) => visibleKeys.has(c.key)),
    [columns, visibleKeys]
  );

  const toggleColumn = (key: string) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (key === 'run_id' || next.size <= 1) return prev;
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const openRun = async (row: TpaRun) => {
    setSelectedId(row.run_id);
    setChartLoading(true);
    setError('');
    try {
      const lot = lotId(row);
      const f = row.fish_id?.trim();
      const s = row.section?.trim();
      const surf = surfaceOf(row);
      if (lot && f && s) {
        const version = Number(row.schema_version || 1);
        const useExperiment = version >= 2 || Boolean(row.experiment_id?.trim());
        const res = await api.tpa.getSeries({
          ...(useExperiment ? { experiment_id: lot } : { group_id: lot }),
          fish_id: f,
          section: s,
          ...(surf ? { surface: surf } : {}),
        });
        setSeries(res.data.runs || []);
        setChartTitle(surf ? `${lot} / ${f} / ${s} / ${surf}` : `${lot} / ${f} / ${s}`);
      } else {
        const res = await api.tpa.getCurve(row.run_id);
        setSeries([res.data]);
        setChartTitle(row.label || row.run_id);
      }
    } catch (e: unknown) {
      setError(errorDetail(e) || 'Failed to load curve');
      setSeries([]);
    } finally {
      setChartLoading(false);
    }
  };

  const xData = useMemo(() => {
    const times = new Set<number>();
    for (const run of series) {
      for (const sample of run.samples || []) times.add(sample.t);
    }
    return Array.from(times)
      .sort((a, b) => a - b)
      .map((t) => ({ t }));
  }, [series]);

  const firmness = useMemo(() => {
    if (!series.length) return null;
    const latest = [...series].sort((a, b) => {
      const da = Number(a.days_since_kill ?? -1);
      const db = Number(b.days_since_kill ?? -1);
      if (da !== db) return da - db;
      return String(a.saved_at || '').localeCompare(String(b.saved_at || ''));
    })[series.length - 1];
    const fromMeta = latest.peak_force_gf;
    if (typeof fromMeta === 'number' && Number.isFinite(fromMeta)) return fromMeta;
    const samples = latest.samples || [];
    if (!samples.length) return null;
    return Math.max(...samples.map((p) => p.gf));
  }, [series]);

  const inputStyle: React.CSSProperties = { padding: '0.4rem 0.6rem', minWidth: 140 };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>TPA Viewer</h2>
      <p style={{ color: '#666', maxWidth: 760 }}>
        Catalog of texture-profile runs from DynamoDB (<code>texture-lab-runs</code>).
        Open a row to overlay force vs time for that fish section and surface (skin vs flesh) across days.
        Lot identity is <code>experiment_id</code> (same as HSI); <code>group_id</code> is a more specific tag.
      </p>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: '#666' }}>experiment_id</span>
          <input
            value={experimentId}
            onChange={(e) => setExperimentId(e.target.value)}
            placeholder="exp-nickbark"
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: '#666' }}>group_id</span>
          <input value={groupId} onChange={(e) => setGroupId(e.target.value)} placeholder="cohort" style={inputStyle} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: '#666' }}>fish_id</span>
          <input value={fishId} onChange={(e) => setFishId(e.target.value)} placeholder="fish" style={inputStyle} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: '#666' }}>section</span>
          <input value={section} onChange={(e) => setSection(e.target.value)} placeholder="B1" style={inputStyle} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: '#666' }}>surface</span>
          <input value={surface} onChange={(e) => setSurface(e.target.value)} placeholder="skin" style={inputStyle} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: '#666' }}>species</span>
          <input value={species} onChange={(e) => setSpecies(e.target.value)} placeholder="SBL" style={inputStyle} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: '#666' }}>frozen_state</span>
          <input
            value={frozenState}
            onChange={(e) => setFrozenState(e.target.value)}
            placeholder="never frozen"
            style={{ ...inputStyle, minWidth: 160 }}
          />
        </label>
        <button
          type="button"
          onClick={loadList}
          disabled={loading}
          style={{
            padding: '0.5rem 1rem',
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <button
          type="button"
          onClick={() => setShowColumnPicker((v) => !v)}
          style={{
            padding: '0.4rem 0.8rem',
            border: '1px solid #dee2e6',
            borderRadius: 4,
            background: '#fff',
            cursor: 'pointer',
          }}
        >
          Columns ({visibleColumns.length}/{columns.length})
        </button>

        {showColumnPicker && (
          <div
            style={{
              marginTop: 8,
              padding: '0.75rem',
              border: '1px solid #dee2e6',
              borderRadius: 4,
              background: '#f8f9fa',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem 1rem',
              maxWidth: 900,
            }}
          >
            <div style={{ width: '100%', display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setVisibleKeys(new Set(columns.map((c) => c.key)))}
                style={{ fontSize: 12, cursor: 'pointer' }}
              >
                Show all
              </button>
              <button
                type="button"
                onClick={() => setVisibleKeys(new Set(PINNED_COLUMNS.map((c) => c.key)))}
                style={{ fontSize: 12, cursor: 'pointer' }}
              >
                Default
              </button>
            </div>

            {columns.map((c) => (
              <label
                key={c.key}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
              >
                <input
                  type="checkbox"
                  checked={visibleKeys.has(c.key)}
                  disabled={c.key === 'run_id'}
                  onChange={() => toggleColumn(c.key)}
                />
                {c.label}
              </label>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div style={{ color: '#b00020', marginBottom: '1rem', whiteSpace: 'pre-wrap' }}>{error}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: selectedId ? '1.15fr 1fr' : '1fr', gap: '1.5rem' }}>
        <div style={{ overflowX: 'auto', border: '1px solid #dee2e6', borderRadius: 4 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8f9fa', textAlign: 'left' }}>
                {visibleColumns.map((c) => (
                  <th key={c.key} style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid #dee2e6' }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRuns.map((row) => (
                <tr
                  key={row.run_id}
                  onClick={() => openRun(row)}
                  style={{
                    cursor: 'pointer',
                    background: selectedId === row.run_id ? '#e7f1ff' : undefined,
                  }}
                >
                  {visibleColumns.map((c) => (
                    <td
                      key={c.key}
                      style={{
                        padding: '0.5rem 0.75rem',
                        borderBottom: '1px solid #f1f3f5',
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {cellValue(row, c.key)}
                    </td>
                  ))}
                </tr>
              ))}
              {visibleRuns.length === 0 && !loading && (
                <tr>
                  <td colSpan={visibleColumns.length} style={{ padding: '1rem', color: '#666' }}>
                    No TPA runs found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {selectedId && (
          <div style={{ border: '1px solid #dee2e6', borderRadius: 4, padding: '1rem' }}>
            <h3 style={{ marginTop: 0 }}>Force vs time</h3>
            {chartTitle && (
              <p style={{ marginTop: 0, fontSize: 13, color: '#666' }}>{chartTitle}</p>
            )}
            {chartLoading && <p style={{ color: '#666' }}>Loading curves…</p>}
            {!chartLoading && series.length === 0 && (
              <p style={{ color: '#666' }}>No curve data for this selection.</p>
            )}
            {!chartLoading && series.some((r) => r.curve_error) && (
              <p style={{ color: '#b00020', fontSize: 13 }}>
                {series
                  .filter((r) => r.curve_error)
                  .map((r) => `${r.run_id}: ${r.curve_error}`)
                  .join('\n')}
              </p>
            )}
            {!chartLoading && xData.length > 0 && (
              <div style={{ width: '100%', height: 440 }}>
                <ResponsiveContainer>
                  <LineChart data={xData} margin={{ top: 8, right: 24, left: 8, bottom: 32 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="t"
                      type="number"
                      domain={['auto', 'auto']}
                      label={{ value: 'Time (sec)', position: 'insideBottom', offset: -16 }}
                    />
                    <YAxis
                      label={{ value: 'Force (g)', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip
                      formatter={(value) =>
                        typeof value === 'number' ? [`${value.toFixed(1)} g`, 'Force'] : value
                      }
                      labelFormatter={(label) => `t = ${Number(label).toFixed(2)} s`}
                    />
                    <Legend
                      verticalAlign="top"
                      align="right"
                      wrapperStyle={{ paddingBottom: 12 }}
                    />
                    {firmness != null && (
                      <ReferenceLine
                        y={firmness}
                        stroke="#333"
                        strokeDasharray="4 4"
                        label={{ value: 'Firmness', position: 'right', fontSize: 12 }}
                      />
                    )}
                    {series.map((run, i) => (
                      <Line
                        key={run.run_id}
                        data={run.samples || []}
                        dataKey="gf"
                        name={seriesName(run, series)}
                        type="monotone"
                        dot={false}
                        stroke={LINE_COLORS[i % LINE_COLORS.length]}
                        strokeWidth={2}
                        connectNulls
                        isAnimationActive={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', marginTop: '1.25rem' }}>
          <button
            type="button"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '0.4rem 1rem',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              background: currentPage === 1 ? '#e9ecef' : '#fff',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
            }}
          >
            Previous
          </button>
          <span style={{ fontSize: '0.9rem', color: '#555' }}>
            Page {currentPage} of {totalPages} ({startIndex + 1}–{endIndex} of {allRuns.length})
          </span>
          <button
            type="button"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: '0.4rem 1rem',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              background: currentPage === totalPages ? '#e9ecef' : '#fff',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default TpaViewer;
